import {inject, injectable} from "inversify";
import {action, computed, makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import {cloneDeep, compact, every, fill, includes, isEmpty, isNull, size, some} from "lodash";
import type {
	IAutoPickTeamPayload,
	IGetTeamPayload,
	IMargin,
	ISetTeamPayload,
	ITeamApiProvider,
	ITeamResponse,
	IUserTeamPayload,
	IUserTeamResponse,
} from "data/providers/api/team.api.provider";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType, RoundStatus} from "data/enums";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {ITradeApiProvider} from "data/providers/api/trade.api.provider";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";

export interface ILineup {
	golfers: number[];
	captain: number;
}

const TRADES_COUNT = 3;

const DEFAULT_LINEUP: ILineup = {
	golfers: fill(Array(6), 0),
	captain: 0,
};

const DEFAULT_TEAM: ITeamResponse = {
	id: null,
	name: "",
	points: null,
	margin: {
		europe: null,
		usa: null,
	},
	lineup: DEFAULT_LINEUP,
	userId: null,
	gamedayId: null,
	availableTrades: 0,
	totalTrades: 0,
	trades: [],
};

const DEFAULT_USER_TEAM: IUserTeamResponse = {
	name: null,
	points: null,
	lineup: null,
	userId: null,
	gamedayId: null,
	gamedayPoints: null,
	gamedayRank: null,
	overallPoints: null,
	overallRank: null,
};

export interface ITeamStore {
	get i18n(): ILocalizationStore;
	get isLoadingTeam(): boolean;
	get lineup(): ILineup;
	get teamObject(): ITeamResponse;
	get tradesArr(): number[];
	get margin(): IMargin;
	get isCurrentLineupFull(): boolean;
	get isCurrentLineupHasPlayer(): boolean;
	get isTeamSaved(): boolean;
	get isTeamChangedNotSaved(): boolean;
	get isTiebreakerVisible(): boolean;
	get isTradeStarted(): boolean;
	get isTradesArrFull(): boolean;
	get isTeamReadyForSave(): boolean;
	get isPromptActive(): boolean;
	get userTeam(): IUserTeamResponse;
	get isAllTradesDone(): boolean;

	addPlayer(playerId: number): void;
	removePlayer(playerId: number): void;
	clearLineup(): void;
	selectCaptain(playerId: number): void;
	getTeam(payload: IGetTeamPayload): Promise<void>;
	setTeam(payload: ISetTeamPayload): Promise<void>;
	autoPickTeam(payload: IAutoPickTeamPayload): Promise<void>;
	saveTiebreaker(): Promise<void>;
	setIsTiebreakerVisible(value: boolean): void;
	setMargin(marginEu: number, marginUs: number): void;
	setIsPromptActive(): void;
	clearIsPromptActive(): void;
	rollbackTrades(): Promise<void>;
	makeTrades(): Promise<void>;
	setPlayerOut(playerId: number): void;
	setPlayerIn(playerId: number): void;
	getUserTeam(payload: IUserTeamPayload): Promise<void>;
	onRollbackLocal: () => void;
	updateCaptain(): Promise<void>;
}

@injectable()
export class TeamStore implements ITeamStore {
	@observable private _isLoading: boolean = false;
	@observable private _teamObject: ITeamResponse = DEFAULT_TEAM;
	@observable private _lineup: ILineup = DEFAULT_LINEUP;
	@observable private _lineupBeforeTrades: ILineup = DEFAULT_LINEUP;
	@observable private _margin: IMargin = {
		europe: null,
		usa: null,
	};
	@observable private _isTeamSaved: boolean = false;
	@observable private _isTeamChangedNotSaved: boolean = false;
	@observable private _isTiebreakerVisible: boolean = false;
	@observable private _isPromptActive: boolean = false;
	@observable private _playersOut: number[] = [];
	@observable private _playersIn: number[] = [];
	@observable private _userTeam: IUserTeamResponse = DEFAULT_USER_TEAM;

	get i18n() {
		return this._i18nStore;
	}

	get lineup() {
		return this._lineup;
	}

	get teamObject() {
		return this._teamObject;
	}

	get tradesArr() {
		return this._playersOut;
	}

	get isAllTradesDone() {
		return (
			this._teamObject.trades.length >= TRADES_COUNT ||
			this.tradesArr.length >= TRADES_COUNT - this._teamObject.trades.length
		);
	}

	get margin() {
		return this._margin;
	}

	get isCurrentLineupFull() {
		return !includes(this._lineup.golfers, 0);
	}

	get isCurrentLineupHasPlayer() {
		return size(compact(this._lineup.golfers)) > 0;
	}

	get isLoadingTeam() {
		return this._isLoading;
	}

	get isTeamSaved() {
		return this._isTeamSaved;
	}

	get isTeamChangedNotSaved() {
		return this._isTeamChangedNotSaved;
	}

	get isTiebreakerVisible() {
		return this._isTiebreakerVisible;
	}

	get isTeamReadyForSave() {
		return every([!this._teamObject.id, this.isCurrentLineupFull, this.lineup.captain]);
	}

	get isPromptActive() {
		return this._isPromptActive;
	}

	@computed get isTradeStarted() {
		if (this._roundStore.selectedRoundSessions.length > 1) {
			return (
				includes(
					[RoundStatus.Playing, RoundStatus.Complete],
					this._roundStore.selectedRoundSessions[0].status
				) && this._roundStore.selectedRoundSessions[1].status === RoundStatus.Scheduled
			);
		}
		return false;
	}

	get isTradesArrFull() {
		return this._teamObject.trades.length >= TRADES_COUNT;
	}

	get userTeam() {
		return this._userTeam;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.TeamApiProvider) private _teamApi: ITeamApiProvider,
		@inject(Bindings.TradeApiProvider) private _tradeApi: ITradeApiProvider,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.RankingsStore) private _rankingsStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	errorMessage = () => {
		return this._modalStore.showModal(ModalType.ERROR, {
			title: this._i18nStore.t("modals.error.title", "Error"),
			message: this._i18nStore.t(
				"modals.error.description",
				"Ups, something went wrong, please try again later."
			),
		});
	};

	@action setIsTiebreakerVisible(value: boolean) {
		this._isTiebreakerVisible = value;
	}

	@action setIsPromptActive() {
		this._isPromptActive = true;
	}

	@action clearIsPromptActive() {
		this._isPromptActive = false;
	}

	@action addPlayer(playerId: number) {
		this.setIsPromptActive();

		if (this.isCurrentLineupFull) {
			return;
		}

		const index = this._lineup.golfers.findIndex((it) => it === 0);
		this._lineup.golfers[index] = playerId;

		this._isTeamChangedNotSaved = true;
	}

	@action removePlayer(playerId: number) {
		this.setIsPromptActive();

		const index = this._lineup.golfers.findIndex((it) => it === playerId);
		this._lineup.golfers[index] = 0;

		this._isTeamChangedNotSaved = true;
	}

	@action selectCaptain(playerId: number) {
		this._lineup.captain = playerId;

		this._isTeamChangedNotSaved = true;
	}

	@action clearLineup() {
		this.setIsPromptActive();

		this._teamObject = DEFAULT_TEAM;
		this._lineup = DEFAULT_LINEUP;
	}

	@action setMargin(marginEu: number, marginUs: number) {
		this._margin = {
			europe: marginEu,
			usa: marginUs,
		};
	}

	@action setPlayerOut(playerId: number) {
		if (this._teamObject.availableTrades === 0 || this._playersOut.length >= 3) {
			return;
		}
		if (isEmpty(this._playersOut)) {
			this._lineupBeforeTrades = cloneDeep(this._lineup);
		}

		const isPlayerInTeam = includes(this._lineupBeforeTrades.golfers, playerId);

		if (isPlayerInTeam) {
			this._playersOut = [...this._playersOut, playerId];
		}

		this._playersIn = this._playersIn.filter((el) => el !== playerId);
	}

	@action setPlayerIn(playerId: number) {
		if (this._teamObject.availableTrades === 0) {
			return;
		}

		this._playersIn = [...this._playersIn, playerId];
	}

	private setDefaultMargin = () => {
		const defaultMargin = this._roundStore.selectedRound!.tiebreakerPoints / 2;
		if (some([isNull(this._teamObject.margin.usa), isNull(this._teamObject.margin.europe)])) {
			this.setMargin(defaultMargin, defaultMargin);
		}
	};

	private createTradePayload = () => {
		return this._playersOut.length
			? this._playersOut.map((id, index) => {
					return {
						playerOut: id,
						playerIn: this._playersIn[index] ?? 0,
					};
			  })
			: [];
	};

	@action onRollbackLocal = () => {
		this._teamObject.lineup = cloneDeep(this._lineupBeforeTrades);
		this._lineup = cloneDeep(this._lineupBeforeTrades);
		this._lineupBeforeTrades = DEFAULT_LINEUP;
		this._playersOut = [];
		this._playersIn = [];
	};

	@action
	async getTeam(payload: IGetTeamPayload) {
		this._isLoading = true;
		try {
			const response = await this._teamApi.getTeam(payload);
			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._margin = team.margin;
			});
		} catch (err) {
			this.errorMessage();
		}
		this._isLoading = false;
	}

	@action
	async setTeam(payload: ISetTeamPayload) {
		this._isLoading = true;
		try {
			const response = await this._teamApi.setTeam(payload);
			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._isTeamChangedNotSaved = false;
				this.setDefaultMargin();
				this.setIsTiebreakerVisible(true);
			});
		} catch (err) {
			this.errorMessage();
		}
		this._isLoading = false;
	}

	@action
	async autoPickTeam(payload: IAutoPickTeamPayload) {
		this._isLoading = true;
		try {
			const response = await this._teamApi.autoPickTeam(payload);
			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._isTeamChangedNotSaved = false;
				this.setDefaultMargin();
				this.setIsTiebreakerVisible(true);
			});
		} catch (err) {
			this.errorMessage();
		}
		this._isLoading = false;
	}

	@action async saveTiebreaker() {
		this._isLoading = true;
		try {
			const response = await this._teamApi.setTiebreaker({
				teamId: this._teamObject.id ?? 0,
				marginEu: this._margin.europe ?? 0,
				marginUsa: this._margin.usa ?? 0,
			});
			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._margin = team.margin;
				this._isTeamChangedNotSaved = false;
				this.setIsTiebreakerVisible(false);
				this.clearIsPromptActive();
			});
		} catch (err) {
			this.errorMessage();
		}
		this._isLoading = false;
	}

	@action async makeTrades() {
		this._isLoading = true;
		try {
			const response = await this._tradeApi.make({
				gamedayId: this._roundStore.selectedRoundId ?? 0,
				captainId: this._lineup.captain,
				trades: this.createTradePayload(),
			});
			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._margin = team.margin;
				this._playersOut = [];
				this._playersIn = [];
				// this._isTeamChangedNotSaved = false;
				// this.setIsTiebreakerVisible(false);
				// this.clearIsPromptActive();
			});
		} catch (err) {
			this.errorMessage();
		}
		this._isLoading = false;
	}

	@action async rollbackTrades() {
		try {
			const response = await this._tradeApi.rollback({
				gamedayId: this._roundStore.selectedRoundId ?? 0,
			});
			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._margin = team.margin;
				this._playersOut = [];
				this._playersIn = [];
				// this._isTeamChangedNotSaved = false;
				// this.setIsTiebreakerVisible(false);
				// this.clearIsPromptActive();
			});
		} catch (err) {
			this.errorMessage();
		}
	}

	@action async getUserTeam(payload: IUserTeamPayload) {
		try {
			const response = await this._teamApi.userTeam(payload);

			const {team} = response.data.success;

			runInAction(() => {
				this._userTeam = team;
			});
		} catch (err) {
			this.errorMessage();
		}
	}

	@action async updateCaptain() {
		try {
			const response = await this._teamApi.updateCaptain({
				teamId: this._teamObject.id ?? 1,
				captainId: this._lineup.captain,
			});

			const {team} = response.data.success;

			runInAction(() => {
				this._lineup = team.lineup || DEFAULT_LINEUP;
				this._teamObject = team;
				this._isTeamChangedNotSaved = false;
			});
		} catch (err) {
			this.errorMessage();
		}
	}
}
